<template>
    <div>
        <ts-page-title
            title="Customer Statement"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                { text: 'Customer Statement', active: true }
            ]"
        />

        <!-- ────────────────── FILTER PANEL ────────────────── -->
        <ts-panel>
            <ts-panel-wrapper class="tw-py-3">
                <div
                    class="tw-flex tw-justify-between tw-items-center tw-cursor-pointer tw-select-none"
                    @click="filtersOpen = !filtersOpen"
                >
                    <div class="tw-flex tw-items-center tw-space-x-3 tw-flex-1 tw-min-w-0">
                        <i
                            class="fas tw-text-gray-400"
                            :class="filtersOpen ? 'fa-chevron-down' : 'fa-chevron-right'"
                            style="width: 12px"
                        ></i>
                        <b class="tw-text-gray-700 tw-whitespace-nowrap">Filter Criteria</b>
                        <div v-if="activeChips.length" class="tw-flex tw-flex-wrap tw-gap-1 tw-flex-1 tw-min-w-0">
                            <span
                                v-for="chip in activeChips"
                                :key="chip.key"
                                class="chip-pill"
                                :class="chip.tone || 'chip-default'"
                            >
                                <i v-if="chip.icon" :class="'fas ' + chip.icon + ' tw-mr-1'"></i>
                                {{ chip.label }}
                            </span>
                        </div>
                    </div>
                    <span class="tw-text-sm tw-text-blue-600 tw-whitespace-nowrap tw-ml-3">
                        {{ filtersOpen ? 'Collapse' : 'Edit filters' }}
                    </span>
                </div>

                <div v-show="filtersOpen" class="tw-mt-3">
                    <!-- Row 1: Currency / Branch / CO -->
                    <div class="filter-grid">
                        <div class="filter-cell" style="grid-column: span 2">
                            <label class="filter-lbl">Currency <span class="text-danger">*</span></label>
                            <div class="pill-group">
                                <button
                                    v-for="c in currencies"
                                    :key="c.currency_id"
                                    type="button"
                                    class="pill"
                                    :class="{ 'pill-active': model.currency_id === c.currency_id }"
                                    @click="model.currency_id = c.currency_id"
                                >{{ c.currency_code }}</button>
                            </div>
                        </div>

                        <div class="filter-cell" style="grid-column: span 4">
                            <label class="filter-lbl">Branch <span class="tw-text-gray-400 tw-font-normal tw-normal-case">(all if empty)</span></label>
                            <ts-branch-filter
                                @filter="value => (model.branch_id = value)"
                                :isPlaceholder="true"
                            />
                        </div>

                        <div class="filter-cell" style="grid-column: span 6">
                            <label class="filter-lbl">Credit Officer</label>
                            <Select
                                v-model="model.employee_id"
                                multiple
                                :max-tag-count="1"
                                :filterable="true"
                                :filter-by-label="true"
                                placeholder="All COs"
                            >
                                <Option
                                    v-for="item in employees"
                                    :value="item.employee_id"
                                    :key="item.employee_id"
                                    :label="item.employee_name_en"
                                >
                                    {{ item.employee_name_en }}
                                </Option>
                            </Select>
                        </div>
                    </div>

                    <!-- Row 2: Loan/customer search — one input, results as a table -->
                    <div class="filter-grid tw-mt-3 tw-items-end">
                        <div class="filter-cell" style="grid-column: span 12">
                            <label class="filter-lbl">Loan or Customer <span class="text-danger">*</span></label>
                            <div class="search-group">
                                <div class="search-input-wrap">
                                    <input
                                        type="text"
                                        class="form-control search-input"
                                        v-model="searchQuery"
                                        placeholder="Type loan #, customer code or customer name — then Find"
                                        @keyup.enter="searchLoan"
                                    />
                                    <button
                                        v-if="searchQuery"
                                        type="button"
                                        class="search-clear"
                                        title="Clear search"
                                        @click.prevent="searchQuery = ''"
                                    ><i class="fas fa-times-circle"></i></button>
                                </div>
                                <button
                                    type="button"
                                    class="search-btn"
                                    :disabled="loanLoading"
                                    @click.prevent="searchLoan"
                                >
                                    <i class="fas fa-circle-notch fa-spin" v-if="loanLoading"></i>
                                    <i class="fas fa-search" v-else></i>
                                    <span class="tw-ml-1">Find</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Loan-picker table (only after a Find) -->
                    <div v-if="loanLoading || loanFindTouched" class="picker-card tw-mt-3">
                        <div class="picker-toolbar">
                            <div class="tw-text-sm tw-text-gray-600">
                                <template v-if="loanLoading">
                                    <i class="fas fa-circle-notch fa-spin tw-mr-1"></i> Searching…
                                </template>
                                <template v-else-if="!loanDisbursements || !loanDisbursements.length">
                                    <i class="far fa-folder-open tw-mr-1"></i> No matching loans
                                </template>
                                <template v-else>
                                    <b>{{ loanDisbursements.length }}</b> match{{ loanDisbursements.length === 1 ? '' : 'es' }} — tick the loans to include
                                    <span v-if="model.loan_id.length"
                                        class="tw-ml-2 tw-text-blue-600 tw-font-semibold">
                                        · {{ model.loan_id.length }} selected
                                    </span>
                                </template>
                            </div>
                            <button
                                v-if="loanDisbursements && loanDisbursements.length"
                                type="button"
                                class="link-btn"
                                @click.prevent="clearLoanSelection"
                            >Clear selection</button>
                        </div>
                        <Table
                            v-if="!loanLoading && loanDisbursements && loanDisbursements.length"
                            :columns="loanPickerColumns"
                            :data="loanDisbursements"
                            border
                            stripe
                            size="small"
                            :height="loanPickerHeight"
                            @on-selection-change="onLoanSelectionChange"
                            ref="loanPicker"
                        ></Table>
                    </div>

                    <!-- Row 3: Actions -->
                    <div class="tw-flex tw-justify-end tw-mt-3 tw-gap-2">
                        <button
                            type="button"
                            class="reset-btn-inline"
                            @click.prevent="onReset"
                        >
                            <i class="fas fa-undo tw-mr-1"></i> Reset
                        </button>
                        <button
                            type="button"
                            class="search-btn-primary"
                            :disabled="loading || !model.loan_id.length"
                            @click.prevent="onSearch"
                        >
                            <i class="fas fa-circle-notch fa-spin" v-if="loading"></i>
                            <i class="fas fa-search" v-else></i>
                            <span class="tw-ml-1">Search</span>
                        </button>
                    </div>
                </div>
            </ts-panel-wrapper>
        </ts-panel>

        <!-- ────────────────── SUMMARY CARDS (3) ────────────────── -->
        <div class="summary-grid tw-mt-3">
            <div
                v-for="card in summaryCards"
                :key="card.key"
                class="summary-card"
                :class="'summary-card--' + card.tone"
            >
                <div class="summary-card__header">
                    <div class="summary-card__icon">
                        <i :class="'fas ' + card.icon"></i>
                    </div>
                    <div class="summary-card__label">{{ card.label }}</div>
                </div>
                <div class="summary-card__value">
                    <template v-if="summaryLoading">
                        <span class="tw-text-gray-300">—</span>
                    </template>
                    <template v-else>
                        <span class="summary-card__amount">{{ fmt(card.total) }}</span>
                        <span class="summary-card__currency">{{ currencyCode }}</span>
                    </template>
                </div>
            </div>
        </div>

        <!-- ────────────────── RESULTS CARD ────────────────── -->
        <div class="results-card tw-mt-3">
            <div class="results-toolbar">
                <div class="tw-flex tw-items-center tw-gap-3 tw-text-sm tw-flex-wrap">
                    <template v-if="loading">
                        <span class="tw-text-gray-500">
                            <i class="fas fa-circle-notch fa-spin tw-mr-1"></i> Loading…
                        </span>
                    </template>
                    <template v-else-if="meta.total === null">
                        <span class="tw-text-gray-400">
                            <i class="fas fa-info-circle tw-mr-1"></i> Pick loan(s) and click Search
                        </span>
                    </template>
                    <template v-else-if="meta.total === 0">
                        <span class="tw-text-gray-500">
                            <i class="far fa-folder-open tw-mr-1"></i> No transactions found
                        </span>
                    </template>
                    <template v-else>
                        <span class="tw-font-bold tw-text-gray-800 tw-text-base">
                            {{ meta.total.toLocaleString() }} entries
                        </span>
                        <span class="tw-text-gray-500">
                            · Page {{ meta.current_page }} of {{ meta.last_page }}
                        </span>
                    </template>
                </div>

                <div class="tw-flex tw-items-center tw-gap-2">
                    <div class="export-group" title="Export the current filters to xlsx">
                        <select v-model="model.excel_template" class="export-select">
                            <option value="standard">Standard xlsx</option>
                            <option value="raw-data">Raw Data xlsx</option>
                        </select>
                        <button
                            type="button"
                            class="export-btn"
                            :disabled="exporting"
                            @click.prevent="exportExcel"
                        >
                            <i class="fas fa-circle-notch fa-spin" v-if="exporting"></i>
                            <i class="far fa-file-excel" v-else></i>
                            <span class="tw-ml-1">Export</span>
                        </button>
                    </div>
                    <button
                        type="button"
                        class="pdf-btn"
                        :disabled="pdfLoading || !model.loan_id.length"
                        @click.prevent="printPdf"
                        title="Print PDF (statement)"
                    >
                        <i class="fas fa-circle-notch fa-spin" v-if="pdfLoading"></i>
                        <i class="far fa-file-pdf" v-else></i>
                        <span class="tw-ml-1">PDF</span>
                    </button>
                </div>
            </div>

            <div
                class="results-body tw-relative"
                :style="{ minHeight: hasRows ? '0px' : emptyBodyHeight + 'px' }"
            >
                <Spin size="large" fix v-if="loading"></Spin>

                <div v-if="!loading && meta.total === null" class="empty-state">
                    <i class="fas fa-search tw-text-2xl tw-mb-2 tw-text-gray-300"></i>
                    <div class="tw-text-sm tw-text-gray-500">Search a loan or customer, select it above, then click <b>Search</b>.</div>
                </div>

                <div v-else-if="!loading && meta.total === 0" class="empty-state">
                    <i class="far fa-folder-open tw-text-2xl tw-mb-2 tw-text-gray-300"></i>
                    <div class="tw-text-sm tw-text-gray-500">No transactions on the selected loans.</div>
                </div>

                <Table
                    v-else
                    :columns="columns"
                    :data="rows"
                    border
                    stripe
                    size="small"
                    :height="tableHeight"
                    @on-sort-change="onSortChange"
                ></Table>
            </div>

            <!-- Full-screen PDF preview -->
            <Modal
                v-model="pdfModalOpen"
                fullscreen
                :closable="true"
                :mask-closable="false"
                :footer-hide="true"
                class-name="pdf-modal"
                @on-cancel="onClosePdfModal"
            >
                <div slot="header" class="tw-flex tw-items-center tw-justify-between">
                    <div class="tw-flex tw-items-center tw-gap-2">
                        <i class="far fa-file-pdf tw-text-red-600"></i>
                        <b>Customer Statement — Preview</b>
                    </div>
                    <div class="tw-flex tw-items-center tw-gap-2 tw-mr-6">
                        <a
                            v-if="pdfFileUrl"
                            :href="pdfFileUrl"
                            target="_blank"
                            rel="noopener"
                            class="pdf-modal-action"
                            title="Open in new tab"
                        >
                            <i class="fas fa-external-link-alt tw-mr-1"></i> Open
                        </a>
                        <a
                            :href="pdfFileUrl || '#'"
                            :download="pdfFileUrl ? '' : null"
                            class="pdf-modal-action"
                            :class="{ 'tw-pointer-events-none tw-opacity-50': !pdfFileUrl }"
                            title="Download PDF"
                        >
                            <i class="fas fa-download tw-mr-1"></i> Download
                        </a>
                    </div>
                </div>
                <div class="pdf-modal-body">
                    <Spin size="large" fix v-if="pdfLoading"></Spin>
                    <iframe
                        v-if="pdfFileUrl"
                        :src="pdfFileUrl"
                        frameborder="0"
                        class="pdf-frame"
                        title="Customer Statement PDF"
                    ></iframe>
                </div>
            </Modal>

            <div v-if="meta.total > 0" class="results-pagination">
                <div class="pagination-left">
                    <span class="pagination-lbl">Per page:</span>
                    <Select
                        v-model="perPage"
                        @on-change="onPerPageChange"
                        style="width: 80px"
                        size="small"
                    >
                        <Option :value="10">10</Option>
                        <Option :value="30">30</Option>
                        <Option :value="50">50</Option>
                        <Option :value="100">100</Option>
                    </Select>
                    <span class="pagination-divider">|</span>
                    <span class="pagination-lbl">
                        Showing <b>{{ rangeStart.toLocaleString() }}</b>–<b>{{ rangeEnd.toLocaleString() }}</b>
                        of <b>{{ meta.total.toLocaleString() }}</b>
                    </span>
                </div>
                <Page
                    :current="meta.current_page"
                    :total="meta.total || 0"
                    :page-size="perPage"
                    @on-change="onPageChange"
                    size="small"
                    show-elevator
                ></Page>
            </div>
        </div>
    </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
import { debounce, join, trim } from 'lodash'
import moment from 'moment'
import * as FileDownload from 'downloadjs'

const TYPE_META = {
    Disbursement:  { color: '#3b82f6', bg: '#eff6ff', border: '#dbeafe' },
    'Admin Fee':   { color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe' },
    'Insurance Fee': { color: '#f59e0b', bg: '#fef3c7', border: '#fde68a' },
    Repayment:     { color: '#10b981', bg: '#ecfdf5', border: '#d1fae5' }
}

export default {
    name: 'customerStatementV3',
    data () {
        return {
            filtersOpen: true,
            loading: false,
            exporting: false,
            pdfLoading: false,
            pdfModalOpen: false,
            pdfFileUrl: '',
            loanLoading: false,
            loanFindTouched: false,
            errors: new Errors(),
            searchQuery: '',
            perPage: 30,
            viewportHeight: window.innerHeight,
            summaryLoading: false,
            summary: {
                opening_balance: '0',
                total_paid:      '0',
                closing_balance: '0'
            },
            rows: [],
            meta: { current_page: 1, last_page: 1, total: null },
            model: {
                branch_id: [],
                employee_id: [],
                loan_id: [],
                currency_id: null,
                sort: 'trans_date',
                sort_dir: 'asc',
                page: 1,
                perPage: 30,
                excel_template: 'standard'
            }
        }
    },
    created () {
        this.model.currency_id = this.$store.state.authUser.sys_setting[0].default_currency_id
    },
    computed: {
        ...mapState('report/resource', ['employees', 'currencies', 'loanDisbursements']),

        currencyCode () {
            if (!this.model.currency_id) return ''
            const c = (this.currencies || []).find(c => c.currency_id === this.model.currency_id)
            return c ? c.currency_code : ''
        },
        isKh () {
            const locale = (this.$i18n && this.$i18n.locale) || localStorage.getItem('langCode')
            return locale === 'kh'
        },

        activeChips () {
            const chips = []
            if (this.model.currency_id) {
                const cur = this.currencies.find(c => c.currency_id === this.model.currency_id)
                if (cur) chips.push({ key: 'currency', label: cur.currency_code, tone: 'chip-currency' })
            }
            if (this.model.loan_id && this.model.loan_id.length) {
                chips.push({
                    key: 'loans', icon: 'fa-file-invoice-dollar',
                    label: this.model.loan_id.length + ' loan' + (this.model.loan_id.length > 1 ? 's' : ''),
                    tone: 'chip-type'
                })
            }
            return chips.filter(c => c.label)
        },

        tableHeight () {
            const chrome = this.filtersOpen ? 640 : 490
            const h = this.viewportHeight - chrome
            return Math.max(280, h)
        },
        emptyBodyHeight () {
            return this.tableHeight + 52
        },
        hasRows () {
            return !this.loading && this.meta.total > 0
        },
        rangeStart () {
            if (!this.meta.total) return 0
            return (this.meta.current_page - 1) * this.perPage + 1
        },
        rangeEnd () {
            if (!this.meta.total) return 0
            return Math.min(this.meta.current_page * this.perPage, this.meta.total)
        },

        summaryCards () {
            return [
                {
                    key: 'opening', tone: 'opening', icon: 'fa-arrow-up',
                    label: 'Opening Balance',
                    total: this.summary.opening_balance
                },
                {
                    key: 'paid', tone: 'paid', icon: 'fa-arrow-down',
                    label: 'Total Paid',
                    total: this.summary.total_paid
                },
                {
                    key: 'closing', tone: 'closing', icon: 'fa-wallet',
                    label: 'Closing Balance',
                    total: this.summary.closing_balance
                }
            ]
        },

        loanPickerHeight () {
            const count = (this.loanDisbursements || []).length
            // ~34px per row + ~42 header — cap at 260 (about 6 rows).
            return Math.min(260, Math.max(120, 42 + count * 34))
        },

        loanPickerColumns () {
            return [
                {
                    type: 'selection', width: 50, align: 'center',
                    renderHeader: h => h('span')
                },
                {
                    title: 'Loan #', key: 'loan_number', width: 160, align: 'center',
                    render: (h, params) =>
                        h('code', { class: 'tw-text-xs tw-text-gray-700' }, params.row.loan_number || '—')
                },
                {
                    title: 'Status', key: 'is_finished', width: 90, align: 'center',
                    render: (h, params) => {
                        return params.row.is_finished
                            ? h('span', { class: 'status-pill status-pill--finished' }, 'Finished')
                            : h('span', { class: 'status-pill status-pill--active' }, 'Active')
                    }
                },
                {
                    title: 'Customer Code', key: 'customer_code', width: 130, align: 'center',
                    render: (h, params) =>
                        h('span', { class: 'tw-text-xs tw-text-gray-700' },
                            (params.row.customer && params.row.customer.customer_code) || '—')
                },
                {
                    title: 'Customer Name', key: 'customer_name', minWidth: 220,
                    render: (h, params) => {
                        const c = params.row.customer || {}
                        const name = this.isKh
                            ? (c.customer_name_kh || c.customer_name_en)
                            : (c.customer_name_en || c.customer_name_kh)
                        return h('span', { class: 'name-text' }, name || '—')
                    }
                },
                {
                    title: 'Phone', key: 'contact_no', width: 140,
                    render: (h, params) =>
                        h('span', { class: 'tw-text-xs tw-text-gray-600' },
                            (params.row.customer && params.row.customer.contact_no) || '—')
                }
            ]
        },

        columns () {
            return [
                {
                    title: 'Branch', key: 'branch_code', width: 100, fixed: 'left', align: 'center',
                    render: (h, params) => h('span', { class: 'branch-code' }, params.row.branch_code || '—')
                },
                {
                    title: 'Loan #', key: 'loan_number', width: 160, fixed: 'left', align: 'center', sortable: 'custom',
                    render: (h, params) => h('code', { class: 'tw-text-xs tw-text-gray-700' }, params.row.loan_number || '—')
                },
                {
                    title: 'Trans Date', key: 'trans_date', width: 115, align: 'center', sortable: 'custom',
                    render: (h, params) => {
                        const d = params.row.trans_date
                        if (!d) return h('span', { class: 'tw-text-gray-300' }, '—')
                        return h('span', { class: 'tw-text-xs' }, moment(d).format('DD MMM YYYY'))
                    }
                },
                {
                    title: 'Schedule', key: 'schedule_date', width: 115, align: 'center', sortable: 'custom',
                    render: (h, params) => {
                        const d = params.row.schedule_date
                        if (!d) return h('span', { class: 'tw-text-gray-300' }, '—')
                        return h('span', { class: 'tw-text-xs' }, moment(d).format('DD MMM YYYY'))
                    }
                },
                {
                    title: 'Receipt', key: 'receipt_no', width: 130, align: 'center',
                    render: (h, params) => h('span', { class: 'tw-text-xs tw-text-gray-600' }, params.row.receipt_no || '—')
                },
                {
                    title: 'Type', key: 'trans_type', width: 130, align: 'center', sortable: 'custom',
                    render: (h, params) => {
                        const t = params.row.trans_type || ''
                        const m = TYPE_META[t] || { color: '#94a3b8', bg: '#f3f4f6', border: '#e5e7eb' }
                        return h('span', {
                            class: 'type-chip',
                            style: { color: m.color, background: m.bg, borderColor: m.border }
                        }, [
                            h('span', { class: 'type-dot', style: { background: m.color } }),
                            t
                        ])
                    }
                },
                {
                    title: 'Amount', key: 'trans_amount', width: 140, align: 'right', sortable: 'custom',
                    render: (h, params) => {
                        const n = parseFloat(params.row.trans_amount)
                        if (isNaN(n) || n === 0) return h('span', { class: 'tw-text-gray-300' }, '—')
                        const isRepayment = params.row.trans_type === 'Repayment'
                        return h('span', {
                            class: isRepayment ? 'tw-font-semibold tw-text-green-700' : 'tw-font-semibold tw-text-gray-800'
                        }, this.fmt(params.row.trans_amount))
                    }
                },
                {
                    title: 'Location', key: 'payment_location', width: 100, align: 'center',
                    render: (h, params) => h('span', { class: 'tw-text-xs tw-text-gray-600' }, params.row.payment_location || '—')
                },
                {
                    title: 'End User', key: 'end_user', minWidth: 140,
                    render: (h, params) => h('span', { class: 'tw-text-xs tw-text-gray-600' }, params.row.end_user || '—')
                },
                {
                    title: 'System Date', key: 'system_date', width: 160, align: 'center',
                    render: (h, params) => {
                        const d = params.row.system_date
                        if (!d) return h('span', { class: 'tw-text-gray-300' }, '—')
                        return h('span', { class: 'tw-text-xs tw-text-gray-500' }, moment(d).format('DD MMM YYYY HH:mm'))
                    }
                }
            ]
        }
    },
    methods: {
        ...mapActions('report/resource', ['getEmployee', 'getCurrency', 'getLoanDisbursement']),

        fmt (v) {
            const n = parseFloat(v)
            if (isNaN(n) || n === 0) return '—'
            return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        },

        searchLoan () {
            const query = (this.searchQuery || '').trim()
            if (query.length < 2) {
                this.notice({ type: 'warning', text: 'Type at least 2 characters to search.' })
                return
            }
            this.loanLoading = true
            this.loanFindTouched = true
            this.model.loan_id = []

            const params = {
                filter: {
                    search: query,
                    co_id: join(this.model.employee_id, ','),
                    approve_status: 2
                },
                branch_id: this.model.branch_id,
                currency_id: this.model.currency_id
            }
            this.getLoanDisbursement(params).finally(() => {
                this.loanLoading = false
            })
        },

        onLoanSelectionChange (selection) {
            this.model.loan_id = (selection || []).map(r => r.loan_id)
        },

        clearLoanSelection () {
            this.model.loan_id = []
            const ref = this.$refs.loanPicker
            if (ref && typeof ref.selectAll === 'function') {
                ref.selectAll(false)
            }
        },

        buildParams () {
            return {
                loan_id:        this.model.loan_id,
                currency_id:    this.model.currency_id,
                sort:           this.model.sort,
                sort_dir:       this.model.sort_dir,
                page:           this.model.page,
                perPage:        this.perPage,
                excel_template: this.model.excel_template
            }
        },

        fetchData () {
            if (!this.model.loan_id.length) {
                this.notice({ type: 'warning', text: 'Pick at least one loan first.' })
                return
            }
            this.errors = new Errors()
            this.loading = true
            this.summaryLoading = true

            this.$store
                .dispatch('report/creditOperation/customerStatementV3', this.buildParams())
                .then(response => {
                    const data    = response.data || []
                    const meta    = response.meta || response
                    const summary = response.summary || null

                    this.rows = data
                    this.meta = {
                        current_page: meta.current_page || 1,
                        last_page:    meta.last_page    || 1,
                        total:        meta.total ?? 0
                    }
                    if (summary) {
                        this.summary = Object.assign(
                            { opening_balance: '0', total_paid: '0', closing_balance: '0' },
                            summary
                        )
                    }
                })
                .catch(error => {
                    this.notice({ type: 'error', text: (error && error.message) || 'Failed to load report' })
                    this.errors = new Errors((error && error.errors) || {})
                    this.rows = []
                })
                .finally(() => {
                    this.loading = false
                    this.summaryLoading = false
                })
        },

        onSearch () {
            this.model.page = 1
            this.filtersOpen = false
            this.fetchData()
        },

        onReset () {
            this.searchQuery = ''
            this.model.loan_id = []
            this.model.employee_id = []
            this.model.sort = 'trans_date'
            this.model.sort_dir = 'asc'
            this.model.page = 1
            this.rows = []
            this.meta = { current_page: 1, last_page: 1, total: null }
            this.summary = { opening_balance: '0', total_paid: '0', closing_balance: '0' }
            this.loanFindTouched = false
        },

        onSortChange ({ key, order }) {
            if (order === 'normal') {
                this.model.sort = 'trans_date'
                this.model.sort_dir = 'asc'
            } else {
                this.model.sort = key
                this.model.sort_dir = order === 'asc' ? 'asc' : 'desc'
            }
            this.model.page = 1
            this.fetchData()
        },

        onPageChange (page) {
            this.model.page = page
            this.fetchData()
        },
        onPerPageChange (v) {
            this.perPage = v
            this.model.page = 1
            this.fetchData()
        },

        exportExcel () {
            this.errors = new Errors()
            this.exporting = true

            this.$store
                .dispatch('report/creditOperation/customerStatementV3ExportExcel', this.buildParams())
                .then(response => {
                    const filename = trim(
                        response.headers['content-disposition']
                            .substring(response.headers['content-disposition'].indexOf('filename'))
                            .replace('filename=', ''),
                        '"'
                    )
                    FileDownload(response.data, filename)
                })
                .catch(err => {
                    let msg = 'Export failed'
                    try {
                        const parsed = JSON.parse(String.fromCharCode.apply(null, new Uint8Array(err)))
                        msg = parsed.message || msg
                        this.errors = new Errors(parsed.errors || {})
                    } catch (e) { /* not JSON */ }
                    this.notice({ type: 'error', text: msg })
                })
                .finally(() => { this.exporting = false })
        },

        printPdf () {
            if (!this.model.loan_id.length) {
                this.notice({ type: 'warning', text: 'Pick at least one loan first.' })
                return
            }
            this.errors = new Errors()
            this.pdfLoading = true
            this.pdfModalOpen = true
            this.pdfFileUrl = ''

            this.$store
                .dispatch('report/creditOperation/customerStatementV3ExportPdf', this.buildParams())
                .then(response => {
                    // Endpoint returns { url } pointing at the generated file.
                    this.pdfFileUrl = (response && response.url) || ''
                    if (!this.pdfFileUrl) {
                        this.notice({ type: 'error', text: 'PDF response was empty' })
                        this.pdfModalOpen = false
                    }
                })
                .catch(err => {
                    const msg = (err && err.message) || 'PDF failed'
                    this.notice({ type: 'error', text: msg })
                    this.pdfModalOpen = false
                })
                .finally(() => { this.pdfLoading = false })
        },

        onClosePdfModal () {
            this.pdfFileUrl = ''
        },

        notice (not) {
            this.$Notice[not.type]({
                title: 'CUSTOMER STATEMENT',
                desc: not.text
            })
        }
    },
    watch: {
        'model.branch_id': debounce(function (value) {
            this.model.employee_id = []
            this.getEmployee({ branch_id: value })
        }, 500)
    },
    mounted () {
        this._onResize = debounce(() => { this.viewportHeight = window.innerHeight }, 100)
        window.addEventListener('resize', this._onResize)
    },
    beforeDestroy () {
        if (this._onResize) window.removeEventListener('resize', this._onResize)
    },
    beforeRouteEnter (to, from, next) {
        next(vm => { vm.getCurrency() })
    }
}
</script>

<style scoped>
.filter-grid {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: 12px;
    align-items: end;
}
.filter-cell { min-width: 0; }
.filter-lbl {
    display: block; font-size: 11px; font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.03em;
    color: #6b7280; margin-bottom: 4px; height: 15px;
}
::v-deep .ivu-select-selection { min-height: 34px; }
::v-deep .ivu-input { height: 34px; }

.pill-group {
    display: inline-flex; align-items: center; flex-wrap: nowrap; gap: 3px;
    padding: 3px; background: #f3f4f6; border-radius: 6px;
    height: 34px; box-sizing: border-box; overflow-x: auto; max-width: 100%;
}
.pill {
    background: transparent; border: none; padding: 4px 10px;
    font-size: 12px; color: #4b5563; border-radius: 4px;
    cursor: pointer; transition: all 0.15s; line-height: 1.2;
    white-space: nowrap;
}
.pill:hover { background: #e5e7eb; }
.pill-active {
    background: #ffffff; color: #111827; font-weight: 600;
    box-shadow: 0 1px 2px rgba(0,0,0,0.08);
}

.chip-pill {
    display: inline-flex; align-items: center; padding: 2px 8px;
    font-size: 11px; border-radius: 10px;
    background: #f3f4f6; color: #374151; line-height: 1.5; white-space: nowrap;
}
.chip-default  { background: #f3f4f6; color: #374151; }
.chip-currency { background: #ecfdf5; color: #047857; }
.chip-type     { background: #dbeafe; color: #1e40af; }

.search-group {
    display: flex; align-items: stretch; height: 34px;
    border: 1px solid #d1d5db; border-radius: 6px;
    overflow: hidden; background: #ffffff;
}
.search-input-wrap { position: relative; flex: 1; display: flex; align-items: stretch; min-width: 0; }
.search-input {
    flex: 1; border: none !important; height: 100% !important;
    padding: 0 32px 0 12px; outline: none; background: transparent;
    font-size: 13px; color: #111827; min-width: 0;
}
.search-input:focus { box-shadow: none !important; }
.search-clear {
    position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
    background: transparent; border: none; padding: 4px;
    font-size: 14px; color: #9ca3af; cursor: pointer;
}
.search-btn {
    background: #6366f1; color: white; border: none;
    padding: 0 16px; font-size: 13px; font-weight: 600;
    cursor: pointer; display: inline-flex; align-items: center;
}
.search-btn:hover:not(:disabled) { background: #4f46e5; }
.search-btn:disabled { background: #a5b4fc; cursor: wait; }

.search-btn-primary {
    background: #3b82f6; color: white; border: none;
    padding: 6px 18px; font-size: 13px; font-weight: 600;
    border-radius: 6px; cursor: pointer;
    display: inline-flex; align-items: center;
}
.search-btn-primary:hover:not(:disabled) { background: #2563eb; }
.search-btn-primary:disabled { background: #93c5fd; cursor: not-allowed; }

.reset-btn-inline {
    background: #f9fafb; color: #6b7280;
    border: 1px solid #e5e7eb; padding: 5px 14px;
    font-size: 13px; border-radius: 6px; cursor: pointer;
}
.reset-btn-inline:hover { background: #f3f4f6; color: #374151; }

/* ── Loan picker card ─────────────────────────────────────────── */
.picker-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    overflow: hidden;
}
.picker-toolbar {
    display: flex; justify-content: space-between; align-items: center;
    padding: 8px 12px; background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
}
.link-btn {
    background: none; border: none;
    color: #3b82f6; font-size: 12px; font-weight: 600;
    cursor: pointer;
}
.link-btn:hover { text-decoration: underline; }

.results-card {
    background: #ffffff; border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); overflow: hidden;
}
.results-toolbar {
    display: flex; justify-content: space-between; align-items: center;
    padding: 12px 16px; border-bottom: 1px solid #e5e7eb;
    background: #ffffff; gap: 8px; flex-wrap: wrap;
}
.results-body { min-height: 120px; }
.results-pagination {
    display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center;
    padding: 10px 16px; border-top: 1px solid #e5e7eb;
    background: #f9fafb; gap: 8px; min-height: 52px;
}
.pagination-left {
    display: flex; align-items: center; gap: 10px;
    height: 28px; line-height: 28px; font-size: 13px; color: #6b7280;
}
.pagination-lbl { display: inline-flex; align-items: center; height: 28px; line-height: 28px; white-space: nowrap; }
.pagination-lbl b { color: #111827; font-weight: 600; margin: 0 2px; }
.pagination-divider { color: #d1d5db; line-height: 28px; }

.summary-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
}
@media (max-width: 767px) { .summary-grid { grid-template-columns: 1fr; } }
.summary-card {
    display: flex; flex-direction: column; justify-content: space-between; gap: 8px;
    padding: 12px 14px; background: #ffffff;
    border: 1px solid #e5e7eb; border-top: 3px solid;
    border-radius: 6px; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    min-height: 92px;
}
.summary-card__header { display: flex; align-items: center; gap: 8px; }
.summary-card__icon {
    width: 22px; height: 22px; display: flex; align-items: center; justify-content: center;
    border-radius: 4px; font-size: 11px; flex-shrink: 0;
}
.summary-card__label {
    flex: 1; font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.03em; color: #6b7280; line-height: 1.2;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.summary-card__value {
    display: flex; align-items: baseline; gap: 6px;
    line-height: 1; white-space: nowrap; overflow: hidden;
}
.summary-card__amount {
    font-size: 20px; font-weight: 700; color: #111827;
    overflow: hidden; text-overflow: ellipsis; min-width: 0;
}
.summary-card__currency {
    font-size: 11px; font-weight: 600; color: #9ca3af;
    letter-spacing: 0.03em; flex-shrink: 0;
}
.summary-card--opening { border-top-color: #3b82f6; }
.summary-card--opening .summary-card__icon { background: #eff6ff; color: #1e40af; }
.summary-card--paid    { border-top-color: #10b981; }
.summary-card--paid    .summary-card__icon { background: #ecfdf5; color: #047857; }
.summary-card--closing { border-top-color: #f59e0b; }
.summary-card--closing .summary-card__icon { background: #fef3c7; color: #92400e; }

::v-deep .ivu-table th {
    background: #f9fafb !important;
    height: 40px; color: #374151; font-weight: 600;
    font-size: 12px; text-transform: uppercase; letter-spacing: 0.02em;
}
::v-deep .ivu-table th .ivu-table-cell {
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
::v-deep .ivu-table td .ivu-table-cell {
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; padding: 4px 12px;
}
::v-deep .ivu-table td { height: 34px; }
::v-deep .ivu-table-small td { height: 32px; }

.export-group {
    display: inline-flex; align-items: stretch; height: 32px;
    border-radius: 5px; overflow: hidden;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    border: 1px solid #d1d5db; background: #ffffff;
}
.export-select {
    border: 0; border-right: 1px solid #d1d5db;
    background: #ffffff; padding: 0 28px 0 12px;
    font-size: 12px; color: #374151; font-weight: 500;
    cursor: pointer; outline: none; height: 100%;
    appearance: none; -webkit-appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3e%3cpath fill='%236b7280' d='M5 6L0 0h10z'/%3e%3c/svg%3e");
    background-repeat: no-repeat; background-position: right 10px center; background-size: 10px 6px;
}
.export-btn {
    background: #10b981; color: white; border: none;
    padding: 0 16px; font-size: 13px; font-weight: 600;
    cursor: pointer; display: inline-flex; align-items: center;
}
.export-btn:hover:not(:disabled) { background: #059669; }
.export-btn:disabled { background: #e5e7eb; color: #9ca3af; cursor: not-allowed; }

.pdf-btn {
    background: #ef4444; color: white; border: none;
    padding: 0 16px; font-size: 13px; font-weight: 600;
    height: 32px; border-radius: 5px;
    cursor: pointer; display: inline-flex; align-items: center;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}
.pdf-btn:hover:not(:disabled) { background: #dc2626; }
.pdf-btn:disabled { background: #fca5a5; cursor: not-allowed; }

.empty-state {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 40px 20px; text-align: center; height: 100%; position: absolute; inset: 0;
}

/* ── PDF preview modal (fullscreen) ───────────────────────────── */
::v-deep .pdf-modal .ivu-modal-body {
    padding: 0;
    height: calc(100vh - 51px);   /* subtract header height */
    background: #525659;          /* same grey PDF viewers use */
}
.pdf-modal-body {
    position: relative;
    width: 100%;
    height: 100%;
    background: #525659;
}
.pdf-frame {
    width: 100%;
    height: 100%;
    display: block;
}
.pdf-modal-action {
    background: #f9fafb;
    color: #374151;
    border: 1px solid #d1d5db;
    padding: 4px 12px;
    font-size: 12px;
    font-weight: 600;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.15s;
}
.pdf-modal-action:hover:not(:disabled) { background: #f3f4f6; }
.pdf-modal-action:disabled { color: #9ca3af; background: #f3f4f6; cursor: not-allowed; }
</style>

<!-- Unscoped: render-function elements don't receive data-v-xxx. -->
<style>
.branch-code {
    display: inline-block; padding: 1px 6px;
    font-size: 11px; font-weight: 600;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    color: #4338ca; background: #eef2ff;
    border: 1px solid #e0e7ff; border-radius: 3px;
    letter-spacing: 0.02em;
}
.type-chip {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 0 8px; height: 20px;
    font-size: 11px; font-weight: 600;
    border-radius: 10px; border: 1px solid;
    letter-spacing: 0.02em; white-space: nowrap;
}
.type-dot {
    display: inline-block; width: 6px; height: 6px;
    border-radius: 50%; flex-shrink: 0;
}
.status-pill {
    display: inline-block;
    padding: 1px 8px;
    font-size: 11px;
    font-weight: 600;
    border-radius: 10px;
    letter-spacing: 0.02em;
    border: 1px solid;
}
.status-pill--active {
    color: #1e40af;
    background: #eff6ff;
    border-color: #dbeafe;
}
.status-pill--finished {
    color: #047857;
    background: #ecfdf5;
    border-color: #d1fae5;
}
</style>
